import { ArrowLeftOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  BarometerInHg,
  BarometerTrend,
  CloudCoverPercent,
  Waterbody,
  Waypoint,
  WaypointFilter,
  WindDirection,
  WindMph,
} from '@omniafishing/core';
import { Collapse, Popconfirm, Select } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useUser } from '../../../../hooks/use_user';
import { useWaypointFilters } from '../../../../hooks/use_waypoint_filters';
import { apiV1 } from '../../../../lib/api';
import { ProUpsellModalActions } from '../../../../redux/pro_upsell_modal';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import SvgLockIcon from '../../../svg/lock_icon';
import { WaypointMarker } from '../../waypoint_marker';
import styles from './filters_screen.less';
import { areWaypointDatesSame } from './waypoint_card';
import { waypointTypes } from './waypoint_form';
import { getLabel } from './waypoint_form_helper';

dayjs.extend(isBetween);

const monthOptions = [
  {
    label: 'January',
    value: 1,
  },
  {
    label: 'February',
    value: 2,
  },
  {
    label: 'March',
    value: 3,
  },
  {
    label: 'April',
    value: 4,
  },
  {
    label: 'May',
    value: 5,
  },
  {
    label: 'June',
    value: 6,
  },
  {
    label: 'July',
    value: 7,
  },
  {
    label: 'August',
    value: 8,
  },
  {
    label: 'September',
    value: 9,
  },
  {
    label: 'October',
    value: 10,
  },
  {
    label: 'November',
    value: 11,
  },
  {
    label: 'December',
    value: 12,
  },
];

const monthsWith31Days = [1, 3, 5, 7, 8, 10, 12];
const monthsWith30Days = [4, 6, 9, 11];

export type WaypointType = Waypoint['waypoint_type'];

const windDirectionOptions: WindDirection[] = [
  'north',
  'northeast',
  'east',
  'southeast',
  'south',
  'southwest',
  'west',
  'northwest',
];
const windSpeedOptions: {
  name: string;
  value: WindMph;
}[] = [
  { name: '0-5 mph', value: '0-5' },
  { name: '6-10 mph', value: '6-10' },
  { name: '11-15 mph', value: '11-15' },
  { name: '16-20 mph', value: '16-20' },
  { name: '20+ mph', value: 'gt_20' },
];
const cloudCoverOptions: {
  name: string;
  value: CloudCoverPercent;
}[] = [
  { name: 'Clear (0–10%)', value: '0-10' },
  { name: 'Mostly Clear (11–30%)', value: '11-30' },
  { name: 'Partly Cloudy (31–70%)', value: '31-70' },
  { name: 'Mostly Cloudy (71–90%)', value: '71-90' },
  { name: 'Overcast (91–100%)', value: '91-100' },
];
const barometricPressureOptions: {
  name: string;
  value: BarometerInHg;
}[] = [
  { name: 'Low (<29.80)', value: 'lt_29.80' },
  { name: 'Medium (29.80-30.20)', value: '29.80-30.20' },
  { name: 'High (>30.20)', value: 'gt_30.20' },
];
const barometricPressureTrendOptions: {
  name: string;
  value: BarometerTrend;
}[] = [
  { name: 'Falling', value: 'falling' },
  { name: 'Steady', value: 'steady' },
  { name: 'Rising', value: 'rising' },
];

interface FiltersScreenForm {
  selectedBarometricPressures: BarometerInHg[];
  selectedBarometricPressureTrends: BarometerTrend[];
  selectedCloudCovers: CloudCoverPercent[];
  selectedEndDateDay: number;
  selectedEndDateMonth: number;
  selectedStartDateDay: number;
  selectedStartDateMonth: number;
  selectedWaterbodyIds: string[];
  selectedWaypointTypes: WaypointType[];
  selectedWindDirections: WindDirection[];
  selectedWindSpeeds: WindMph[];
}

const FILTERS = {
  SELECTED_BAROMETRIC_PRESSURE_TRENDS: 'selectedBarometricPressureTrends',
  SELECTED_BAROMETRIC_PRESSURES: 'selectedBarometricPressures',
  SELECTED_CLOUD_COVERS: 'selectedCloudCovers',
  SELECTED_END_DATE_MONTH: 'selectedEndDateMonth',
  SELECTED_END_DATE_DAY: 'selectedEndDateDay',
  SELECTED_START_DATE_MONTH: 'selectedStartDateMonth',
  SELECTED_START_DATE_DAY: 'selectedStartDateDay',
  SELECTED_WATERBODY_IDS: 'selectedWaterbodyIds',
  SELECTED_WAYPOINT_TYPES: 'selectedWaypointTypes',
  SELECTED_WIND_DIRECTIONS: 'selectedWindDirections',
  SELECTED_WIND_SPEEDS: 'selectedWindSpeeds',
} as const;

export const isWaypointWithinDateRange = (waypoint: Waypoint, dateRange: string[]) => {
  if (dateRange.length === 0) {
    return true;
  }
  const [startDate, endDate] = dateRange;
  const waypointDate = areWaypointDatesSame(waypoint)
    ? dayjs(waypoint.created_at)
    : dayjs(waypoint.outing_at);

  return waypointDate.isBetween(dayjs(startDate), dayjs(endDate), 'day', '[]');
};

export interface FiltersScreenProps {
  defaultSelectedBarometricPressures: BarometerInHg[];
  defaultSelectedBarometricPressureTrends: BarometerTrend[];
  defaultSelectedCloudCovers: CloudCoverPercent[];
  defaultSelectedEndDate: string;
  defaultSelectedStartDate: string;
  defaultSelectedWaterbodyIds: string[];
  defaultSelectedWaypointTypes: WaypointType[];
  defaultSelectedWindDirections: WindDirection[];
  defaultSelectedWindSpeeds: WindMph[];
  onFilterChange: (filters: {
    selectedBarometricPressures: BarometerInHg[];
    selectedBarometricPressureTrends: BarometerTrend[];
    selectedCloudCovers: CloudCoverPercent[];
    selectedEndDate: string | null;
    selectedStartDate: string | null;
    selectedWaterbodyIds: string[];
    selectedWaypointTypes: WaypointType[];
    selectedWindDirections: WindDirection[];
    selectedWindSpeeds: WindMph[];
  }) => void;
  onClose: () => void;
  onModalClose: () => void;
  savedFilterApplied: WaypointFilter;
  onSavedFilterApplied: (filter: WaypointFilter) => void;
  waypoints: Waypoint[];
}

export const FiltersScreen = (props: FiltersScreenProps) => {
  const {
    defaultSelectedBarometricPressures,
    defaultSelectedBarometricPressureTrends,
    defaultSelectedCloudCovers,
    defaultSelectedEndDate,
    defaultSelectedStartDate,
    defaultSelectedWaterbodyIds,
    defaultSelectedWaypointTypes,
    defaultSelectedWindDirections,
    defaultSelectedWindSpeeds,
    onSavedFilterApplied,
    savedFilterApplied,
    onClose,
    onFilterChange,
    onModalClose,
    waypoints,
  } = props;

  const { waypointFilters, deleteWaypointFilter } = useWaypointFilters();
  const { isPro } = useUser();
  const dispatch = useDispatch();

  const [maxStartDateDay, setMaxStartDateDay] = useState(31);
  const [maxEndDateDay, setMaxEndDateDay] = useState(31);
  const [minEndDateDay, setMinEndDateDay] = useState(1);
  const [minEndDateMonth, setMinEndDateMonth] = useState(12);

  const { register, watch, handleSubmit, setValue, control } = useForm({
    defaultValues: {
      [FILTERS.SELECTED_BAROMETRIC_PRESSURE_TRENDS]: defaultSelectedBarometricPressureTrends,
      [FILTERS.SELECTED_BAROMETRIC_PRESSURES]: defaultSelectedBarometricPressures,
      [FILTERS.SELECTED_CLOUD_COVERS]: defaultSelectedCloudCovers,
      [FILTERS.SELECTED_END_DATE_DAY]: defaultSelectedEndDate
        ? Number(defaultSelectedEndDate.split('-')[1])
        : null,
      [FILTERS.SELECTED_END_DATE_MONTH]: defaultSelectedEndDate
        ? Number(defaultSelectedEndDate.split('-')[0])
        : null,
      [FILTERS.SELECTED_START_DATE_DAY]: defaultSelectedStartDate
        ? Number(defaultSelectedStartDate.split('-')[1])
        : null,
      [FILTERS.SELECTED_START_DATE_MONTH]: defaultSelectedStartDate
        ? Number(defaultSelectedStartDate.split('-')[0])
        : null,
      [FILTERS.SELECTED_WATERBODY_IDS]: defaultSelectedWaterbodyIds,
      [FILTERS.SELECTED_WAYPOINT_TYPES]: defaultSelectedWaypointTypes,
      [FILTERS.SELECTED_WIND_DIRECTIONS]: defaultSelectedWindDirections,
      [FILTERS.SELECTED_WIND_SPEEDS]: defaultSelectedWindSpeeds,
    },
  });

  const selectedBarometricPressures = watch(FILTERS.SELECTED_BAROMETRIC_PRESSURES);
  const selectedBarometricPressureTrends = watch(FILTERS.SELECTED_BAROMETRIC_PRESSURE_TRENDS);
  const selectedCloudCovers = watch(FILTERS.SELECTED_CLOUD_COVERS);
  const selectedEndDateDay = watch(FILTERS.SELECTED_END_DATE_DAY);
  const selectedEndDateMonth = watch(FILTERS.SELECTED_END_DATE_MONTH);
  const selectedEndDate =
    selectedEndDateDay && selectedEndDateMonth
      ? `${selectedEndDateMonth}-${selectedEndDateDay}`
      : null;
  const selectedStartDateDay = watch(FILTERS.SELECTED_START_DATE_DAY);
  const selectedStartDateMonth = watch(FILTERS.SELECTED_START_DATE_MONTH);
  const selectedStartDate =
    selectedStartDateDay && selectedStartDateMonth
      ? `${selectedStartDateMonth}-${selectedStartDateDay}`
      : null;
  const selectedWaterbodyIds = watch(FILTERS.SELECTED_WATERBODY_IDS);
  const selectedWaypointTypes = watch(FILTERS.SELECTED_WAYPOINT_TYPES);
  const selectedWindDirections = watch(FILTERS.SELECTED_WIND_DIRECTIONS);
  const selectedWindSpeeds = watch(FILTERS.SELECTED_WIND_SPEEDS);

  const hasDefaultDateRange = !!defaultSelectedStartDate && !!defaultSelectedEndDate;
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(hasDefaultDateRange);

  const handleCustomDateSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const valueCustomDateRange = e.target.value === 'custom_dates';
    setShowCustomDatePicker(valueCustomDateRange);
    if (valueCustomDateRange) {
      setValue(FILTERS.SELECTED_START_DATE_DAY, null);
      setValue(FILTERS.SELECTED_END_DATE_DAY, null);
      setValue(FILTERS.SELECTED_START_DATE_MONTH, null);
      setValue(FILTERS.SELECTED_END_DATE_MONTH, null);
    }
  }, []);

  const onApplyFilters: SubmitHandler<FiltersScreenForm> = (data: FiltersScreenForm) => {
    onFilterChange({
      selectedWaterbodyIds: data.selectedWaterbodyIds,
      selectedWaypointTypes: data.selectedWaypointTypes,
      selectedWindDirections: data.selectedWindDirections,
      selectedWindSpeeds: data.selectedWindSpeeds,
      selectedCloudCovers: data.selectedCloudCovers,
      selectedBarometricPressures: data.selectedBarometricPressures,
      selectedBarometricPressureTrends: data.selectedBarometricPressureTrends,
      selectedStartDate:
        data.selectedStartDateDay && data.selectedStartDateMonth
          ? `${data.selectedStartDateMonth}-${data.selectedStartDateDay}`
          : null,
      selectedEndDate:
        data.selectedEndDateDay && data.selectedEndDateMonth
          ? `${data.selectedEndDateMonth}-${data.selectedEndDateDay}`
          : null,
    });
  };

  const waterbodiesInWaypoints: Waterbody[] = useMemo(() => {
    return waypoints.reduce((acc, waypoint) => {
      if (waypoint.waterbody && !acc.some((wb) => wb.id === waypoint.waterbody.id)) {
        acc.push(waypoint.waterbody);
      }
      return acc;
    }, []);
  }, [waypoints]);

  const waypointTypesInWaypoints = useMemo(() => {
    const _waypointTypes = waypoints.reduce((acc, wp) => {
      if (!acc.includes(wp.waypoint_type)) {
        acc.push(wp.waypoint_type);
      }
      return acc;
    }, [] as WaypointType[]);

    return _.sortBy(_waypointTypes, (wt) => waypointTypes.indexOf(wt));
  }, [waypoints]);

  const alphaSortedWaterbodiesInWaypoints = useMemo(() => {
    return _.sortBy(waterbodiesInWaypoints, (wb) => wb.primary_name);
  }, [waterbodiesInWaypoints]);

  const [filteredWaypointsCount, setFilteredWaypointsCount] = useState(0);
  useEffect(() => {
    apiV1
      .userWaypointsFetch({
        barometer_in_hgs: selectedBarometricPressures,
        barometer_trends: selectedBarometricPressureTrends,
        cloud_cover_percents: selectedCloudCovers,
        end_date: selectedEndDate,
        start_date: selectedStartDate,
        waterbodies: selectedWaterbodyIds,
        waypoint_types: selectedWaypointTypes,
        wind_directions: selectedWindDirections,
        wind_mphs: selectedWindSpeeds,
      })
      .then((res) => {
        setFilteredWaypointsCount(res.data.data.length);
      });
  }, [
    selectedBarometricPressures,
    selectedBarometricPressureTrends,
    selectedCloudCovers,
    selectedEndDate,
    selectedStartDate,
    selectedWaterbodyIds,
    selectedWaypointTypes,
    selectedWindDirections,
    selectedWindSpeeds,
  ]);

  // ensure end date is after start date
  useEffect(() => {
    let newMaxStartDateDay = maxStartDateDay;
    if (selectedStartDateMonth === 2) {
      newMaxStartDateDay = 28;
    } else if (monthsWith31Days.includes(selectedStartDateMonth)) {
      newMaxStartDateDay = 31;
    } else if (monthsWith30Days.includes(selectedStartDateMonth)) {
      newMaxStartDateDay = 30;
    }
    setMaxStartDateDay(newMaxStartDateDay);

    let newMaxEndDateDay = maxEndDateDay;
    if (selectedEndDateMonth === 2) {
      newMaxEndDateDay = 28;
    } else if (monthsWith31Days.includes(selectedEndDateMonth)) {
      newMaxEndDateDay = 31;
    } else if (monthsWith30Days.includes(selectedEndDateMonth)) {
      newMaxEndDateDay = 30;
    }
    setMaxEndDateDay(newMaxEndDateDay);

    const selectedStartDateDayNumber = Number(selectedStartDateDay);
    const selectedEndDateDayNumber = Number(selectedEndDateDay);

    // if current start day is greater than the new max, set it to the new max
    if (selectedStartDateDayNumber > newMaxStartDateDay) {
      setValue(FILTERS.SELECTED_START_DATE_DAY, newMaxStartDateDay);
    }
    if (selectedEndDateDayNumber > newMaxEndDateDay) {
      setValue(FILTERS.SELECTED_END_DATE_DAY, newMaxEndDateDay);
    }

    // ensure end date is after start date
    let newMinEndDateDay: number;
    if (selectedEndDateMonth === selectedStartDateMonth) {
      newMinEndDateDay = selectedStartDateDayNumber + 1;
      if (selectedEndDateDayNumber < selectedStartDateDayNumber) {
        setValue(FILTERS.SELECTED_END_DATE_DAY, newMinEndDateDay);
      }
    } else {
      newMinEndDateDay = 1;
    }
    if (newMinEndDateDay > newMaxStartDateDay) {
      setValue(FILTERS.SELECTED_END_DATE_DAY, 1);
      setValue(FILTERS.SELECTED_END_DATE_MONTH, selectedEndDateMonth + 1);
    }
    setMinEndDateDay(newMinEndDateDay);

    let newMinEndDateMonth = selectedStartDateMonth ? minEndDateMonth : 1;
    if (selectedEndDateMonth < selectedStartDateMonth) {
      setValue(FILTERS.SELECTED_END_DATE_MONTH, selectedStartDateMonth);
      newMinEndDateMonth = selectedStartDateMonth;
    }
    setMinEndDateMonth(newMinEndDateMonth);
  }, [
    selectedEndDateDay,
    selectedEndDateMonth,
    selectedStartDateDay,
    selectedStartDateMonth,
    minEndDateDay,
    maxStartDateDay,
    maxEndDateDay,
    minEndDateMonth,
  ]);

  const headerText = (text: string, count: number, showLock?: boolean) => {
    return (
      <span className="flex">
        <span className="flex-1">{text + (count > 0 ? ` (${count})` : '')}</span>
        {showLock && <SvgLockIcon />}
      </span>
    );
  };

  const applySavedFilterValues = (filter: WaypointFilter) => {
    setValue(FILTERS.SELECTED_BAROMETRIC_PRESSURES, filter.barometer_in_hgs);
    setValue(FILTERS.SELECTED_BAROMETRIC_PRESSURE_TRENDS, filter.barometer_trends);
    setValue(FILTERS.SELECTED_CLOUD_COVERS, filter.cloud_cover_percents);
    setValue(
      FILTERS.SELECTED_END_DATE_DAY,
      filter.end_date ? Number(filter.end_date.split('-')[1]) : null
    );
    setValue(
      FILTERS.SELECTED_END_DATE_MONTH,
      filter.end_date ? Number(filter.end_date.split('-')[0]) : null
    );
    setValue(
      FILTERS.SELECTED_START_DATE_DAY,
      filter.start_date ? Number(filter.start_date.split('-')[1]) : null
    );
    setValue(
      FILTERS.SELECTED_START_DATE_MONTH,
      filter.start_date ? Number(filter.start_date.split('-')[0]) : null
    );
    setValue(
      FILTERS.SELECTED_WATERBODY_IDS,
      filter.waterbodies.map((w) => w.id)
    );
    setValue(FILTERS.SELECTED_WAYPOINT_TYPES, filter.waypoint_types);
    setValue(FILTERS.SELECTED_WIND_DIRECTIONS, filter.wind_directions);
    setValue(FILTERS.SELECTED_WIND_SPEEDS, filter.wind_mphs);
    handleSubmit(onApplyFilters)();
  };

  return (
    <>
      <div className={styles.modalHeader}>
        <span role="button" tabIndex={0} aria-label="Go back a screen" className={styles.goBackBtn}>
          <ArrowLeftOutlined onClick={onClose} />
        </span>
        <span className={styles.heading}>Filters</span>
        <CloseOutlined onClick={onModalClose} />
      </div>
      <div className={styles.body}>
        <Collapse expandIconPosition="end" ghost>
          <Collapse.Panel
            key="saved_filters"
            header={headerText('Saved Filters', waypointFilters.length, !isPro)}
          >
            {isPro ? (
              <div>
                {waypointFilters.length > 0 ? (
                  <ul className="flex flex-col gap-2">
                    {waypointFilters.map((filter) => {
                      return (
                        <li key={filter.id} className="flex justify-between items-center">
                          <span
                            className="flex-1 h-full py-1 cursor-pointer"
                            onClick={() => {
                              applySavedFilterValues(filter);
                              onClose();
                              onSavedFilterApplied(filter);
                            }}
                          >
                            {filter.name}
                          </span>
                          <div className="flex gap-2">
                            <OmniaButton
                              shape="circle"
                              size="md"
                              kind={savedFilterApplied?.id === filter.id ? 'secondary' : 'tertiary'}
                              onPress={() => {
                                applySavedFilterValues(filter);
                                onSavedFilterApplied(filter);
                              }}
                            >
                              <EditOutlined />
                            </OmniaButton>
                            <Popconfirm
                              title="Are you sure?"
                              okText="Delete"
                              cancelText="Cancel"
                              onConfirm={() => {
                                deleteWaypointFilter.mutate(filter.id);
                              }}
                            >
                              <OmniaButton shape="circle" size="md" kind="tertiary">
                                <DeleteOutlined />
                              </OmniaButton>
                            </Popconfirm>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="italic">No saved filters</p>
                )}
              </div>
            ) : (
              <>
                <p className="mb-1">
                  <strong>Save Advanced Waypoint Filters</strong> with Omnia PRO
                </p>
                <OmniaButton
                  onPress={() => {
                    dispatch(ProUpsellModalActions.PRO_UPSELL_MODAL_OPEN());
                  }}
                  kind="primary"
                  size="md"
                >
                  Learn More
                </OmniaButton>
              </>
            )}
          </Collapse.Panel>
        </Collapse>
        <Collapse expandIconPosition="end" ghost defaultActiveKey={['waypont_type']}>
          <Collapse.Panel
            key="waypont_type"
            header={headerText('Waypoint Type', selectedWaypointTypes.length)}
          >
            <fieldset>
              <div className={styles.waypointTypeList}>
                {waypointTypesInWaypoints.map((wt) => {
                  const typeIsSelected = selectedWaypointTypes.includes(wt);
                  return (
                    <label htmlFor={wt} key={wt} className={styles.waypointType}>
                      <input
                        type="checkbox"
                        id={wt}
                        value={wt}
                        {...register(FILTERS.SELECTED_WAYPOINT_TYPES)}
                      />{' '}
                      <span className={styles.waypointMarker}>
                        <WaypointMarker waypoint_type={wt} />
                      </span>
                      <span
                        className={classNames(styles.waypointTypeLabel, {
                          [styles.waypointTypeLabel__selected]: typeIsSelected,
                        })}
                      >
                        {getLabel(wt)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          </Collapse.Panel>
        </Collapse>
        <section className={styles.waterbodies}>
          <Collapse expandIconPosition="end" ghost>
            <Collapse.Panel
              key="waterbodies"
              header={headerText('Waterbody', selectedWaterbodyIds.length)}
            >
              {alphaSortedWaterbodiesInWaypoints.map((wb) => (
                <div className={styles.checkboxRow} key={wb.id}>
                  <label htmlFor={wb.primary_name} className={styles.label}>
                    <input
                      className={styles.checkboxNativeInput}
                      id={wb.primary_name}
                      type="checkbox"
                      value={wb.id}
                      {...register(FILTERS.SELECTED_WATERBODY_IDS)}
                    />
                    <span className={styles.checkboxOverlay} />
                    <span className={styles.labelText}>{wb.primary_name}</span>
                  </label>
                </div>
              ))}
            </Collapse.Panel>
          </Collapse>
        </section>
        <section className={styles.dateFilters}>
          <Collapse expandIconPosition="end" ghost>
            <Collapse.Panel key="outing_at" header="Outing Date">
              <fieldset>
                <div className={styles.dateCreatedToggle}>
                  <label htmlFor="all_dates">
                    <input
                      type="radio"
                      id="all_dates"
                      value={'all_dates'}
                      checked={!showCustomDatePicker}
                      onChange={handleCustomDateSelect}
                    />
                    Any Dates
                  </label>
                  <label htmlFor="custom_dates">
                    <input
                      type="radio"
                      id="custom_dates"
                      value={'custom_dates'}
                      checked={showCustomDatePicker}
                      onChange={handleCustomDateSelect}
                    />
                    Custom Dates
                  </label>
                </div>
              </fieldset>
              {showCustomDatePicker && (
                <div className={styles.pickerWrapper}>
                  <div className={styles.dateWrapper}>
                    <label htmlFor="start_date_month" className={styles.dateLabel}>
                      Start Date
                    </label>
                    <Controller
                      name={FILTERS.SELECTED_START_DATE_MONTH}
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Select
                          options={monthOptions}
                          onChange={onChange}
                          placeholder="Select Month"
                          value={selectedStartDateMonth}
                          className={styles.monthSelect}
                        />
                      )}
                    />
                    <Controller
                      name={FILTERS.SELECTED_START_DATE_DAY}
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Select
                          options={Array.from({ length: maxStartDateDay }, (val, i) => ({
                            label: i + 1,
                            value: i + 1,
                          }))}
                          onChange={onChange}
                          placeholder="Select Day"
                          value={selectedStartDateDay}
                          className={styles.daySelect}
                        />
                      )}
                    />
                  </div>
                  <div className={styles.dateWrapper}>
                    <label htmlFor="end_date_month" className={styles.dateLabel}>
                      End Date
                    </label>
                    <Controller
                      name={FILTERS.SELECTED_END_DATE_MONTH}
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Select
                          options={monthOptions.filter((m) => m.value >= minEndDateMonth)}
                          onChange={onChange}
                          placeholder="Select Month"
                          value={selectedEndDateMonth}
                          className={styles.monthSelect}
                        />
                      )}
                    />
                    <Controller
                      name={FILTERS.SELECTED_END_DATE_DAY}
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Select
                          options={Array.from(
                            { length: maxEndDateDay - minEndDateDay + 1 },
                            (val, i) => ({
                              label: minEndDateDay + i,
                              value: minEndDateDay + i,
                            })
                          )}
                          onChange={onChange}
                          placeholder="Select Day"
                          value={selectedEndDateDay}
                          className={styles.daySelect}
                        />
                      )}
                    />
                  </div>
                </div>
              )}
            </Collapse.Panel>
          </Collapse>
        </section>
        <Collapse expandIconPosition="end" ghost>
          <Collapse.Panel
            key="conditions"
            header={headerText(
              'Conditions',
              selectedWindDirections.length +
                selectedWindSpeeds.length +
                selectedCloudCovers.length +
                selectedBarometricPressures.length +
                selectedBarometricPressureTrends.length,
              !isPro
            )}
          >
            {isPro ? (
              <div className={styles.nestedCollapseContent}>
                <section className={styles.windDirection}>
                  <Collapse expandIconPosition="end" ghost>
                    <Collapse.Panel
                      key="wind_direction"
                      header={headerText('Wind Direction (out of)', selectedWindDirections.length)}
                    >
                      {windDirectionOptions.map((wd) => (
                        <div className={styles.checkboxRow} key={wd}>
                          <label htmlFor={wd} className={styles.label}>
                            <input
                              className={styles.checkboxNativeInput}
                              id={wd}
                              type="checkbox"
                              value={wd}
                              {...register(FILTERS.SELECTED_WIND_DIRECTIONS)}
                            />
                            <span className={styles.checkboxOverlay} />
                            <span className={styles.labelText}>{wd}</span>
                          </label>
                        </div>
                      ))}
                    </Collapse.Panel>
                  </Collapse>
                </section>
                <section className={styles.windSpeed}>
                  <Collapse expandIconPosition="end" ghost>
                    <Collapse.Panel
                      key="wind_speed"
                      header={headerText('Wind Speed', selectedWindSpeeds.length)}
                    >
                      {windSpeedOptions.map((ws) => (
                        <div className={styles.checkboxRow} key={ws.value}>
                          <label htmlFor={ws.value} className={styles.label}>
                            <input
                              className={styles.checkboxNativeInput}
                              id={ws.value}
                              type="checkbox"
                              value={ws.value}
                              {...register(FILTERS.SELECTED_WIND_SPEEDS)}
                            />
                            <span className={styles.checkboxOverlay} />
                            <span className={styles.labelText}>{ws.name}</span>
                          </label>
                        </div>
                      ))}
                    </Collapse.Panel>
                  </Collapse>
                </section>
                <section className={styles.cloudCover}>
                  <Collapse expandIconPosition="end" ghost>
                    <Collapse.Panel
                      key="cloud_cover"
                      header={headerText('Cloud Cover', selectedCloudCovers.length)}
                    >
                      {cloudCoverOptions.map((cc) => (
                        <div className={styles.checkboxRow} key={cc.value}>
                          <label htmlFor={cc.value} className={styles.label}>
                            <input
                              className={styles.checkboxNativeInput}
                              id={cc.value}
                              type="checkbox"
                              value={cc.value}
                              {...register(FILTERS.SELECTED_CLOUD_COVERS)}
                            />
                            <span className={styles.checkboxOverlay} />
                            <span className={styles.labelText}>{cc.name}</span>
                          </label>
                        </div>
                      ))}
                    </Collapse.Panel>
                  </Collapse>
                </section>
                <section className={styles.baroPressure}>
                  <Collapse expandIconPosition="end" ghost>
                    <Collapse.Panel
                      key="baro_pressure"
                      header={headerText('Barometric Pressure', selectedBarometricPressures.length)}
                    >
                      {barometricPressureOptions.map((bp) => (
                        <div className={styles.checkboxRow} key={bp.value}>
                          <label htmlFor={bp.value} className={styles.label}>
                            <input
                              className={styles.checkboxNativeInput}
                              id={bp.value}
                              type="checkbox"
                              value={bp.value}
                              {...register(FILTERS.SELECTED_BAROMETRIC_PRESSURES)}
                            />
                            <span className={styles.checkboxOverlay} />
                            <span className={styles.labelText}>{bp.name}</span>
                          </label>
                        </div>
                      ))}
                    </Collapse.Panel>
                  </Collapse>
                </section>
                <section className={styles.baroTrend}>
                  <Collapse expandIconPosition="end" ghost>
                    <Collapse.Panel
                      key="baro_trend"
                      header={headerText(
                        'Barometric Trend',
                        selectedBarometricPressureTrends.length
                      )}
                    >
                      {barometricPressureTrendOptions.map((bp) => (
                        <div className={styles.checkboxRow} key={bp.value}>
                          <label htmlFor={bp.value} className={styles.label}>
                            <input
                              className={styles.checkboxNativeInput}
                              id={bp.value}
                              type="checkbox"
                              value={bp.value}
                              {...register(FILTERS.SELECTED_BAROMETRIC_PRESSURE_TRENDS)}
                            />
                            <span className={styles.checkboxOverlay} />
                            <span className={styles.labelText}>{bp.name}</span>
                          </label>
                        </div>
                      ))}
                    </Collapse.Panel>
                  </Collapse>
                </section>
              </div>
            ) : (
              <>
                <p className="mb-1">
                  Filter by <strong>Wind, Cloud Cover, Barometric Pressure</strong> and More with
                  Omnia PRO!
                </p>
                <OmniaButton
                  onPress={() => {
                    dispatch(ProUpsellModalActions.PRO_UPSELL_MODAL_OPEN());
                  }}
                  kind="primary"
                  size="md"
                >
                  Learn More
                </OmniaButton>
              </>
            )}
          </Collapse.Panel>
        </Collapse>
      </div>

      <div className={styles.footerWrapper}>
        <div className={styles.footer}>
          <OmniaButton
            onClick={(e) => {
              e.stopPropagation();
              setValue(FILTERS.SELECTED_WATERBODY_IDS, []);
              setValue(FILTERS.SELECTED_WAYPOINT_TYPES, []);
              setValue(FILTERS.SELECTED_START_DATE_DAY, null);
              setValue(FILTERS.SELECTED_END_DATE_DAY, null);
              setValue(FILTERS.SELECTED_START_DATE_MONTH, null);
              setValue(FILTERS.SELECTED_END_DATE_MONTH, null);
              setValue(FILTERS.SELECTED_WIND_DIRECTIONS, []);
              setValue(FILTERS.SELECTED_WIND_SPEEDS, []);
              setValue(FILTERS.SELECTED_CLOUD_COVERS, []);
              setValue(FILTERS.SELECTED_BAROMETRIC_PRESSURES, []);
              setValue(FILTERS.SELECTED_BAROMETRIC_PRESSURE_TRENDS, []);
              handleSubmit(onApplyFilters)();
              onSavedFilterApplied(undefined);
              onClose();
            }}
            kind="tertiary"
            size="md"
            block
          >
            Clear
          </OmniaButton>

          <OmniaButton
            onClick={(e) => {
              e.stopPropagation();
              handleSubmit(onApplyFilters)();
              onClose();
            }}
            kind="primary"
            size="md"
            block
          >
            Show Results ({filteredWaypointsCount})
          </OmniaButton>
        </div>
      </div>
    </>
  );
};
